/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import axiosInstance from 'apis/axiosConfig';

const EditUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        last_name: '',
        father_name: '',
        mother_name: '',
        mobile: '',
        email: '',
        dob: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        pan_number: '',
        aadhaar_number: '',
        bank_account_number: '',
        ifsc_code: '',
        bank_name: '',
        account_holder_name: '',
        monthly_income: '',
        loan_category: 'Individual',
        is_active: 1
    });

    useEffect(() => {
        const fetchUserDetails = async () => {
            setLoading(true);
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/';
                
                // Fetch full customer records
                const listRes = await axios.get(`${backendUrl}customer`);
                let cust = null;
                if (listRes?.data && Array.isArray(listRes.data)) {
                    cust = listRes.data.find(c => parseInt(c.id) === parseInt(id));
                } else if (listRes?.data?.data && Array.isArray(listRes.data.data)) {
                    cust = listRes.data.data.find(c => parseInt(c.id) === parseInt(id));
                }

                if (cust) {
                    setFormData({
                        name: cust.name || '',
                        last_name: cust.last_name || '',
                        father_name: cust.father_name || '',
                        mother_name: cust.mother_name || '',
                        mobile: cust.mobile || '',
                        email: cust.email || '',
                        dob: cust.dob || '',
                        address: cust.address || '',
                        city: cust.city || '',
                        state: cust.state || '',
                        zip_code: cust.zip_code || '',
                        pan_number: cust.pan_number || '',
                        aadhaar_number: cust.aadhaar_number || '',
                        bank_account_number: cust.bank_account_number || '',
                        ifsc_code: cust.ifsc_code || '',
                        bank_name: cust.bank_name || '',
                        account_holder_name: cust.account_holder_name || '',
                        monthly_income: cust.monthly_income || '',
                        loan_category: cust.loan_category || 'Individual',
                        is_active: cust.is_active === 1 || cust.is_active === true ? 1 : 0
                    });
                } else {
                    try {
                        const singleRes = await axios.get(`${backendUrl}customer/one/${id}`);
                        if (singleRes?.data) {
                            const singleCust = singleRes.data?.data || singleRes.data;
                            setFormData({
                                name: singleCust.name || '',
                                last_name: singleCust.last_name || '',
                                father_name: singleCust.father_name || '',
                                mother_name: singleCust.mother_name || '',
                                mobile: singleCust.mobile || '',
                                email: singleCust.email || '',
                                dob: singleCust.dob || '',
                                address: singleCust.address || '',
                                city: singleCust.city || '',
                                state: singleCust.state || '',
                                zip_code: singleCust.zip_code || '',
                                pan_number: singleCust.pan_number || '',
                                aadhaar_number: singleCust.aadhaar_number || '',
                                bank_account_number: singleCust.bank_account_number || '',
                                ifsc_code: singleCust.ifsc_code || '',
                                bank_name: singleCust.bank_name || '',
                                account_holder_name: singleCust.account_holder_name || '',
                                monthly_income: singleCust.monthly_income || '',
                                loan_category: singleCust.loan_category || 'Individual',
                                is_active: singleCust.is_active === 1 || singleCust.is_active === true ? 1 : 0
                            });
                        }
                    } catch (singleErr) {
                        console.error("Single resource fetch failed:", singleErr);
                    }
                }
            } catch (err) {
                console.error("Failed to load user info:", err);
                toast.error("Error retrieving unified borrower record profile.");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchUserDetails();
        }
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                monthly_income: parseFloat(formData.monthly_income) || 0,
                is_active: parseInt(formData.is_active)
            };

            await axiosInstance.patch(`customer/${id}`, payload);
            toast.success("Borrower Profile Details Updated Successfully!");
            navigate(`/users/update/${id}`);
        } catch (err) {
            console.error("Update failed:", err);
            toast.error("Failed to update profile details.");
        } finally {
            setSaving(false);
        }
    };

    const labelStyle = {
        fontSize: '12px',
        color: '#64748B',
        fontWeight: '700',
        textTransform: 'uppercase',
        display: 'block',
        marginBottom: '4px'
    };

    const inputStyle = {
        borderRadius: '10px',
        border: '1px solid #CBD5E1',
        padding: '12px 14px',
        fontSize: '15px',
        color: '#0F172A',
        backgroundColor: '#F8FAFC',
        fontWeight: '600',
        width: '100%'
    };

    const sectionTitleStyle = {
        fontSize: '18px',
        fontWeight: '800',
        color: '#1E293B',
        borderBottom: '2px solid #E2E8F0',
        paddingBottom: '10px',
        marginBottom: '20px',
        marginTop: '30px'
    };

    if (loading) {
        return (
            <div className="container-fluid mt-5 pt-5 text-center">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status" />
                <h5 className="mt-3 font-weight-bold text-secondary">Loading Profile Form...</h5>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4 mb-5" style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
            {/* Top Navigation Bar */}
            <div className="d-flex justify-content-between alignItems-center mb-4 px-2">
                <button 
                    type="button"
                    className="btn btn-sm font-weight-bold" 
                    style={{ backgroundColor: '#F1F5F9', color: '#475569', borderRadius: '8px', padding: '8px 16px' }}
                    onClick={() => navigate(`/users/update/${id}`)}
                >
                    ← Cancel & Return to View
                </button>
                <h4 className="font-weight-bold mb-0" style={{ color: '#0F172A' }}>✏️ Update Borrower Profile Form</h4>
            </div>

            <div className="card border-0 shadow-sm bg-white p-4 p-md-5" style={{ borderRadius: '20px' }}>
                <form onSubmit={handleSubmit}>
                    {/* SECTION 1: Personal Details */}
                    <div style={{ ...sectionTitleStyle, marginTop: 0 }}>Edit Demographics & Lineage Details</div>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_name">First Name</label>
                            <input style={inputStyle} id="edit_name" name="name" type="text" value={formData.name} onChange={handleChange} required />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_last_name">Last Name</label>
                            <input style={inputStyle} id="edit_last_name" name="last_name" type="text" value={formData.last_name} onChange={handleChange} required />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_father_name">Father&apos;s Name</label>
                            <input style={inputStyle} id="edit_father_name" name="father_name" type="text" value={formData.father_name} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_mother_name">Mother&apos;s Name</label>
                            <input style={inputStyle} id="edit_mother_name" name="mother_name" type="text" value={formData.mother_name} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_dob">Date of Birth</label>
                            <input style={inputStyle} id="edit_dob" name="dob" type="date" value={formData.dob} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_loan_category">Loan Category Mapping</label>
                            <select style={inputStyle} id="edit_loan_category" name="loan_category" value={formData.loan_category} onChange={handleChange}>
                                <option value="Individual">Individual Borrower</option>
                                <option value="Business">Business / Commercial Enterprise</option>
                            </select>
                        </div>
                    </div>

                    {/* SECTION 2: Contact Matrix */}
                    <div style={sectionTitleStyle}>Edit Contact Attributes & Domicile Address</div>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_mobile">Verified Mobile Phone</label>
                            <input style={inputStyle} id="edit_mobile" name="mobile" type="text" value={formData.mobile} onChange={handleChange} required />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_email">Email Gateway</label>
                            <input style={inputStyle} id="edit_email" name="email" type="email" value={formData.email} onChange={handleChange} />
                        </div>
                        <div className="col-12 mb-4">
                            <label style={labelStyle} htmlFor="edit_address">Full Domicile Street Address</label>
                            <textarea style={{ ...inputStyle, minHeight: '100px' }} id="edit_address" name="address" value={formData.address} onChange={handleChange} />
                        </div>
                        <div className="col-md-4 mb-4">
                            <label style={labelStyle} htmlFor="edit_city">City</label>
                            <input style={inputStyle} id="edit_city" name="city" type="text" value={formData.city} onChange={handleChange} />
                        </div>
                        <div className="col-md-4 mb-4">
                            <label style={labelStyle} htmlFor="edit_state">State / Province</label>
                            <input style={inputStyle} id="edit_state" name="state" type="text" value={formData.state} onChange={handleChange} />
                        </div>
                        <div className="col-md-4 mb-4">
                            <label style={labelStyle} htmlFor="edit_zip_code">Postal Zip Code</label>
                            <input style={inputStyle} id="edit_zip_code" name="zip_code" type="text" value={formData.zip_code} onChange={handleChange} />
                        </div>
                    </div>

                    {/* SECTION 3: Identity Keys */}
                    <div style={sectionTitleStyle}>Edit Government Identity Verification Keys</div>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_pan_number">PAN Identifier Key</label>
                            <input style={{ ...inputStyle, textTransform: 'uppercase' }} id="edit_pan_number" name="pan_number" type="text" value={formData.pan_number} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_aadhaar_number">Aadhar Key Index</label>
                            <input style={inputStyle} id="edit_aadhaar_number" name="aadhaar_number" type="text" value={formData.aadhaar_number} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_monthly_income">Stated Monthly Income (₹)</label>
                            <input style={inputStyle} id="edit_monthly_income" name="monthly_income" type="number" value={formData.monthly_income} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_is_active">Account Clearance Clearance Policy</label>
                            <select 
                                style={{ ...inputStyle, backgroundColor: formData.is_active ? '#ECFDF5' : '#FFFBEB', color: formData.is_active ? '#065F46' : '#B45309' }} 
                                id="edit_is_active" 
                                name="is_active" 
                                value={formData.is_active} 
                                onChange={handleChange}
                            >
                                <option value={1}>✓ CLEARANCE ACTIVE (Approved)</option>
                                <option value={0}>⏳ CLEARANCE SUSPENDED (Pending Audit Review)</option>
                            </select>
                        </div>
                    </div>

                    {/* SECTION 4: Bank Remittance Parameters */}
                    <div style={sectionTitleStyle}>Edit Bank Remittance Destination Keys</div>
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_bank_name">Bank Institution Name</label>
                            <input style={inputStyle} id="edit_bank_name" name="bank_name" type="text" value={formData.bank_name} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_account_holder_name">Account Holder Name</label>
                            <input style={inputStyle} id="edit_account_holder_name" name="account_holder_name" type="text" value={formData.account_holder_name} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_bank_account_number">Authorized Account Number</label>
                            <input style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '16px', fontWeight: '700' }} id="edit_bank_account_number" name="bank_account_number" type="text" value={formData.bank_account_number} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <label style={labelStyle} htmlFor="edit_ifsc_code">IFSC Interbank Key</label>
                            <input style={{ ...inputStyle, textTransform: 'uppercase' }} id="edit_ifsc_code" name="ifsc_code" type="text" value={formData.ifsc_code} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Bottom Action Footer */}
                    <div className="mt-5 pt-4 border-top text-right">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary font-weight-bold px-4 py-3 mr-3" 
                            style={{ borderRadius: '10px' }}
                            onClick={() => navigate(`/users/update/${id}`)}
                            disabled={saving}
                        >
                            Cancel Changes
                        </button>
                        <button 
                            type="submit" 
                            className="btn btn-success font-weight-bold px-5 py-3 shadow-sm" 
                            style={{ borderRadius: '10px', fontSize: '16px', backgroundColor: '#10B981', border: 0 }}
                            disabled={saving}
                        >
                            {saving ? 'Saving Details...' : '✓ Save & Update Profile Details'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUser;
